import {
  Paper,
  Theme,
  ThemeOptions,
  Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridColDef, GridColumnVisibilityModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import {
  DataGridPremium,
  GridColumnResizeParams, GridFilterItem, GridFilterModel,
  GridRowParams,
  GridSortModel,
  useGridApiRef,
  GridDensity
} from '@mui/x-data-grid-premium';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { format } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import { FC, memo, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { SubmitHandler } from 'react-hook-form';
import { CustomLoadingOverlay } from 'src/components/utils/CustomLoadingOverlay';
import { appBarHeight } from 'src/constants/layout';
import LicenseContext from 'src/contexts/LicenseContext';
import { ProductEntity } from 'src/entities/ProductEntity';
import { RansackPredicateEntity } from 'src/entities/RansackPredicate.entity';
import { queryKey, useProductsRequest as usePaginationRequest } from 'src/hooks/useProducts.request';
import { useMutationProduct } from 'src/hooks/useQueryProducts';
import { dataGridUtil } from 'src/utils/dataGrid.util';
import operatorValueUtil from 'src/utils/operatorValue.util';
import stringUtil from 'src/utils/string.util';

import { clearRowSelection } from '../common/clearRowSelection';
import { SelectAllSnackbars } from '../common/SelectAllSnackbars';

import { Columns } from './Columns';
import CustomToolbar from './CustomToolbar';
import FormDialog from './FormDialog';

interface CustomToolbarProps {
  toggleDialogIsOpen: () => void;
  onClickDeleteProducts: () => void;
  setIsLoading: (bool: boolean) => void;
  handleDownloadCSV: () => Promise<void>;
  density: GridDensity;
  onDensityChange: (density: GridDensity) => void;
}

const V2ProductsPresenter: FC = memo(() => {
  const licenseContext = useContext(LicenseContext);
  const theme: Theme & ThemeOptions = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(30);
  const [rowCount, setRowCount] = useState<number>(0);
  const [sortCondition, setSortCondition] = useState<string>('');
  const [orConditions, setOrConditions] = useState<RansackPredicateEntity[]>([]);
  const [andConditions, setAndConditions] = useState<RansackPredicateEntity[]>([]);
  const [searchCondition, setSearchCondition] = useState<string>('');
  const [editEntity, setEditEntity] = useState<ProductEntity|null>(null);
  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [density, setDensity] = useState<GridDensity>('standard');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const apiRef = useGridApiRef();

  const { data: requestData, isLoading: requestIsLoading } = usePaginationRequest(page, sortCondition, searchCondition);
  const { data: allRequestData, refetch: refetchAllData } = usePaginationRequest(0, sortCondition, searchCondition, Number.MAX_SAFE_INTEGER, { enabled: false });
  const { deleteProducts } = useMutationProduct();

  const localStorageItemPrefix = 'product-columns';

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length === 0) {
      setSortCondition('');
      return;
    }

    const { field } = sortModel[0];

    setSortCondition(
      [
        stringUtil.toSnakeCase(field),
        sortModel[0].sort
      ].join('+')
    );
  };

  const onFilterChange = ((filterModel: GridFilterModel) => {
    const items = filterModel?.items;

    if (!items || items.length === 0) {
      setOrConditions([]);
      setAndConditions([]);
      return;
    }

    const ransackPredicates: RansackPredicateEntity[] = items.flatMap((item: GridFilterItem) => operatorValueUtil.convertToRansackPredicate(
        stringUtil.toSnakeCase(item.field),
        item.operator,
        item.value as string | string[] | undefined,
        filterModel.logicOperator
      )).filter((maybe) => !!maybe);

    setOrConditions(ransackPredicates.filter((predicate) => predicate?.link === 'or'));
    setAndConditions(ransackPredicates.filter((predicate) => predicate?.link === 'and'));
  });

  const openNewProductForm = () => {
    setEditEntity(null);
    setDialogIsOpen(true);
  };

  const onClickDeleteProducts = () => {
    if (selectedIds.length === 0) {
      enqueueSnackbar('削除する商品を選択してください');
      return;
    }

    const confirmMsg = `選択された ${selectedIds.length} 件の商品を削除します。よろしいですか？`;

    if (window.confirm(confirmMsg)) {
      setIsLoading(true);
      deleteProducts.mutateAsync(selectedIds)
        .then(() => clearRowSelection(apiRef, setSelectedIds))
        .catch((e: AxiosError<{ message: string; }>) => {
          enqueueSnackbar(e.response?.data?.message);
          setIsLoading(false);
        })
        .finally(() => {
          void queryClient.invalidateQueries({
            queryKey: [queryKey]
          })
            .finally(() => {
              setIsLoading(false);
            });
        });
    }
    setDialogIsOpen(false);
  };

  const openEdit: SubmitHandler<ProductEntity> = async (
    data
    // eslint-disable-next-line @typescript-eslint/require-await
  ): Promise<void> => {
    setEditEntity(data);
    setDialogIsOpen(true);
  };

  const dataGridColumns: GridColDef[] = useMemo(() => Columns(
    openEdit
  ).filter((col) => {
    if (licenseContext?.config?.selectable_companies?.length > 1) {
      return true;
    }
    return col.field !== 'company_name';
  }), [licenseContext?.config]);

  const onSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    const ids = Array.from(rowSelectionModel.ids);
    if (rowSelectionModel.type === 'include') {
      setSelectedIds(ids.map((it) => Number(it)));
    } else if (rowSelectionModel.type === 'exclude') {
      const allIds = requestData?.data.map((it) => it.id) || [];
      setSelectedIds(allIds.filter((id) => !ids.includes(id)));
    }
  };

  const onRowDoubleClick = (params: GridRowParams) => {
    openEdit(params.row as ProductEntity);
  };

  const columnVisibilityModel = () => {
    const savedColumnVisibilities = dataGridUtil.getSavedColumnVisibilities(localStorageItemPrefix);
    if (Object.keys(savedColumnVisibilities).length === 0) return {};

    return savedColumnVisibilities;
  };

  useEffect(() => {
    setSearchCondition(
      [
        operatorValueUtil.convertToQueryParams('or_conditions', orConditions),
        operatorValueUtil.convertToQueryParams('and_conditions', andConditions),
      ].filter((maybe) => maybe).join('&')
    );
  }, [andConditions, orConditions]);

  useEffect(() => {
    if (!requestData) return;
    setRowCount(requestData?.totalCount || 0);
  }, [requestData]);

  const handleDownloadCSV = async () => {
    setIsLoading(true);
    try {
      const options = {
        utf8WithBom: true,
        allColumns: true,
        fileName: `LOG-商品一覧-${format(new Date(), 'yyyyMMddHHmmss', { timeZone: 'Asia/Tokyo' })}`,
      };
      const { data: fetchedAllData } = await refetchAllData();

      apiRef.current.setRows(fetchedAllData.data);
      apiRef.current.exportDataAsCsv(options);
      apiRef.current.setRows(requestData.data);
    } catch (error) {
      console.error('CSV出力エラー:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>商品管理</title>
      </Helmet>
      <Box p={1}>
        {
          dialogIsOpen && (
            <FormDialog
              isOpen={dialogIsOpen}
              onClose={() => {
                setDialogIsOpen(false);
              }}
              updateIsLoading={(bool: boolean) => {
                setIsLoading(bool);
              }}
              editEntity={editEntity}
            />
          )
        }
        <SelectAllSnackbars
          totalPageCount={requestData?.totalPageCount || 1}
          totalCount={requestData?.totalCount || 0}
          perPage={pageSize}
          allIds={allRequestData?.data.map((it) => it.id) || requestData?.allIds || []}
          selectedIds={selectedIds}
          apiRef={apiRef}
          setSelectedIds={setSelectedIds}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}
        >
          <Box flexGrow={1}>
            <Paper
              style={{
                width: '100%',
                height: `calc(100vh - ${theme.spacing(4)} - ${appBarHeight}px)`
              }}
            >
              <DataGridPremium
                apiRef={apiRef}
                rows={requestData?.data || []}
                columns={dataGridColumns}
                pagination
                checkboxSelection
                disableRowGrouping
                density={density}
                onDensityChange={setDensity}
                initialState={{
                  columns: { columnVisibilityModel: columnVisibilityModel() }
                }}
                slots={{
                  loadingOverlay: CustomLoadingOverlay,
                  toolbar: CustomToolbar
                }}
                slotProps={{
                  toolbar: {
                    toggleDialogIsOpen: openNewProductForm,
                    onClickDeleteProducts,
                    setIsLoading,
                    handleDownloadCSV,
                    density,
                    onDensityChange: setDensity
                  } as Partial<CustomToolbarProps>
                }}
                localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                loading={isLoading || requestIsLoading}
                onRowSelectionModelChange={onSelectionModelChange}
                onRowDoubleClick={onRowDoubleClick}
                disableRowSelectionOnClick
                sortingMode="server"
                paginationMode="server"
                filterMode="server"
                paginationModel={{ page, pageSize }}
                rowCount={rowCount}
                onPaginationModelChange={(model) => {
                  setPage(model.page);
                  setPageSize(model.pageSize);
                  // ページ遷移時は選択をリセットする。state だけでなくグリッド内部の選択モデルも
                  // クリアしないと、全選択(exclude)が次ページへ引き継がれ選択カウントだけ残る。
                  clearRowSelection(apiRef, setSelectedIds);
                }}
                onSortModelChange={handleSortModelChange}
                onFilterModelChange={onFilterChange}
                onColumnWidthChange={(params: GridColumnResizeParams) => {
                  dataGridUtil.setSavedColumnWidth(localStorageItemPrefix, params);
                }}
                onColumnVisibilityModelChange={(newModel: GridColumnVisibilityModel) => {
                  dataGridUtil.setSavedColumnVisibilities(localStorageItemPrefix, newModel);
                }}
                keepNonExistentRowsSelected
                disableAggregation
                disablePivoting
                showToolbar
              />
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
});

export default V2ProductsPresenter;
